import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CircleBtn, CircleBtnType } from "../../../../../../../react-core/Html/CircleBtn";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { BenefitCrystallisationEventTotal } from "../../../../../../../Wrap/Models/Domains/Drawdown/BenefitCrystallisationEventTotal";
import { PrimaryPclsUsage } from "../../../../../../../Wrap/Models/Domains/Drawdown/PrimaryPclsUsage";
import { PensionProtectionDetails } from "../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import AriaTooltip from "../../../../../../Shared/Tooltip/AriaTooltip";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { hasPrimaryProtection } from "./Helper/DrawdownDetailHelper";
import { TaxYear } from "./TaxYear";

interface IBceAndPclsTaxYearProps extends IProcessComponentProps {
    data: DrawdownModel;
    pensionProtection: PensionProtectionDetails;
    process: ProcessType;
}

interface IBceAndPclsTaxYearState {
    hasChanged: boolean;
}

export class BceAndPclsTaxYear extends ProcessComponent<IBceAndPclsTaxYearProps, IBceAndPclsTaxYearState> {
    constructor(props: any) {
        super(props);
        this.state = {
            hasChanged: false,
        };
    }

    private onTaxYearChange(v, index, field) {
        const _data = this.props.data[field].concat();
        if (v == -1) {
            _data.splice(index, 1);
        } else {
            for (const key in v) {
                _data[index][key] = v[key];
            }
        }
        if (field == "BceOffPlatform") {
            this.setState({ hasChanged: true });
        }
        this.props.onChange({ [field]: _data });
    }

    private renderTaxYear(data: PrimaryPclsUsage[] | BenefitCrystallisationEventTotal[], key) {
        const renderedYears = data.map((y) => y.Year);
        return data.map((ele, index) => {
            return (
                <div key={`tax-year-${index}`}>
                    <TaxYear
                        data={ele}
                        excludeYears={renderedYears}
                        onChange={(v) => this.onTaxYearChange(v, index, key)}
                        onRef={(ref) => this.appendChild(ref)}
                    />
                    <CircleBtn
                        type={CircleBtnType.remove}
                        cssWrapper="mr-52"
                        title="Remove this tax year"
                        onClick={() => this.onTaxYearChange(-1, index, key)}
                    />
                </div>
            );
        });
    }

    private showBce = (pensionProtection) => {
        if (this.props.data?.PensionOption != PensionOption.UFPLS) {return false;}
        return (
            hasPrimaryProtection(pensionProtection) ||
            (pensionProtection?.PensionEnhancedProtectionDetails &&
                !pensionProtection?.PensionEnhancedProtectionDetails.PclsEntitlement)
        );
    };

    private showPCLS = (data: DrawdownModel, pensionProtection) => {
        return hasPrimaryProtection(pensionProtection) && data?.PensionOption != PensionOption.UFPLS ? true : false;
    };

    public render(): JSX.Element {
        const { data, onChange, pensionProtection } = this.props;

        return (
            <>
                {this.showPCLS(data, pensionProtection) && (
                    <>
                        {data?.OnPlatformPcls && data?.OnPlatformPcls.length > 0 && (
                            <>
                                <div className="d-flex">
                                    <h4>Protected PCLS taken on platform to date per tax year</h4>
                                    <AriaTooltip cssWrapper="is-info-icon ml-6">
                                        If the figures taken on platform are incorrect and you know the values for the
                                        relevant years, add the difference as an off platform amount in the
                                        corresponding year. If you are unsure whether the figures are correct, or have
                                        any other query on the figures, please contact the Admin team.
                                    </AriaTooltip>
                                </div>
                                <Typography
                                    config={data.OnPlatformPcls.map((ele) => {
                                        return {
                                            title: `${ele.Year}/${ele.Year + 1}`,
                                            data: MoneyDisplayHelper.getMoneyFormat(ele.Amount),
                                        };
                                    })}
                                    type="common"
                                />
                            </>
                        )}
                        <div className="d-flex">
                            <h4>Protected PCLS taken off platform to date per tax year</h4>
                            <AriaTooltip cssWrapper="is-info-icon ml-6">
                                If the figures taken on platform are incorrect and you know the values for the relevant
                                years, add the difference as an off platform amount in the corresponding year. If you
                                are unsure whether the figures are correct, or have any other query on the figures,
                                please contact the Admin team.
                            </AriaTooltip>
                        </div>
                        <RadiosEditor
                            options={[true, false]}
                            cssWrapper={"rg-inline mb-10"}
                            onChange={(v) => onChange({ OffPlatformPcls: v ? [{ Amount: null, Year: 2006 }] : null })}
                            selected={data?.OffPlatformPcls && data?.OffPlatformPcls.length > 0 ? true : false}
                            label="Has any protected PCLS been taken off platform since 2006 / 2007?"
                            getText={(o) => (o ? "Yes" : "No")}
                            required={"Please select"}
                        />
                        {data?.OffPlatformPcls && data.OffPlatformPcls.length > 0 && (
                            <>
                                {this.renderTaxYear(data.OffPlatformPcls, "OffPlatformPcls")}
                                <CircleBtn
                                    type={CircleBtnType.add}
                                    title="Add another tax year"
                                    onClick={() => {
                                        const _data = this.props.data.OffPlatformPcls.concat();
                                        _data.push({ Amount: null, Year: null });
                                        this.props.onChange({ OffPlatformPcls: _data });
                                    }}
                                />
                            </>
                        )}
                        <hr className="mb-20" />
                    </>
                )}
                {this.showBce(pensionProtection) && (
                    <>
                        {data.BceOnPlatform && data.BceOnPlatform.length > 0 && (
                            <>
                                <div className="d-flex">
                                    <h4>Benefit crystallisations performed on platform to date per tax year</h4>
                                    <AriaTooltip cssWrapper="is-info-icon ml-6">
                                        <p>
                                            The 'Notional Value of Benefits from before 6 April 2006' for a pension
                                            scheme member is the total calculated for all of the pension schemes that
                                            fall into any of the categories shown below.
                                        </p>
                                        <p>
                                            It only includes a pension paid to the original member of the scheme. It
                                            does not include any form of survivor’s pension paid after the death of the
                                            original member.
                                        </p>
                                        <p>
                                            <b>
                                                For any approved pension that came into payment before 6 April 2006 (and
                                                became a registered pension on 6 April 2006) that was providing either a
                                                scheme pension or a lifetime annuity at the first benefit
                                                crystallisation event (BCE) date (after 5 April 2006)
                                            </b>
                                        </p>
                                        <p>
                                            If the member had not passed their 75th birthday at the BCE date, the
                                            notional value was 25 x the total gross annual amount of the scheme pension,
                                            or lifetime annuity, at the BCE date.
                                        </p>
                                        <p>
                                            <b>
                                                For any approved pension that came into payment before 6 April 2006 (and
                                                became a registered pension on 6 April 2006) that was providing any form
                                                of drawdown at the first BCE date (after 5 April 2006)
                                            </b>
                                        </p>
                                        <p>The following rules apply.</p>
                                        <p>
                                            <b>
                                                1) If the first BCE occurred in the period from 6 April 2006 to 5 April
                                                2015 (inclusive), the member had not passed their 75th birthday at the
                                                BCE date and the pre-6 April 2006 pension was providing:
                                            </b>
                                        </p>
                                        <p>
                                            <b>a) capped drawdown (including nil income), </b>the notional value was 25
                                            x the gross maximum annual capped drawdown income limit at the BCE date; and
                                        </p>
                                        <p>
                                            <b>b) flexible drawdown (including nil income),</b> the notional value was
                                            25 x the maximum gross annual capped drawdown pension that would have been
                                            paid in the drawdown pension year in which the flexible drawdown declaration
                                            was accepted if that declaration had not been made.
                                        </p>
                                        <p>
                                            <b>
                                                2) If the first BCE occurred after 5 April 2015 and the client had not
                                                passed their 75th birthday at the BCE date and any approved pre-6 April
                                                2015 pension was providing:
                                            </b>
                                        </p>
                                        <p>
                                            <b>a) capped drawdown (including nil income), </b>the notional value was 80%
                                            x 25 x the gross capped drawdown maximum income limit at the BCE date; and
                                        </p>
                                        <p>
                                            <b>
                                                b) capped drawdown (including nil income) at 5 April 2015, which became
                                                flexi-access drawdown on, or after, 6 April 2015,{" "}
                                            </b>
                                            the notional value was 80% x 25 x the maximum gross annual capped drawdown
                                            pension that would have been paid in the drawdown pension year in which the
                                            capped drawdown fund was converted to a flexi-access fund if that conversion
                                            had not been made.
                                        </p>
                                        <p>
                                            <b>
                                                c) flexible drawdown (including nil income) at 5 April 2015, which
                                                became flexi-access drawdown from 6 April 2015, and the flexible
                                                drawdown declaration was accepted in a drawdown pension year that began:
                                            </b>
                                        </p>
                                        <p>
                                            <b> i) before 27 March 2014, </b>the notional value was 25 x the maximum
                                            gross annual capped drawdown pension that would have been paid in the
                                            drawdown pension year in which the flexible drawdown declaration was
                                            accepted if the flexible drawdown declaration had not been made; and
                                        </p>
                                        <p>
                                            <b>ii) after 26 March 2014, </b>the notional value was 80% x 25 x the
                                            maximum gross annual capped drawdown pension that would have been paid in
                                            the drawdown pension year in which the flexible drawdown declaration was
                                            accepted if the flexible drawdown declaration had not been made.
                                        </p>
                                    </AriaTooltip>
                                </div>
                                <Typography
                                    config={data.BceOnPlatform.map((ele) => {
                                        return {
                                            title: `${ele.Year}/${ele.Year + 1}`,
                                            data: MoneyDisplayHelper.getMoneyFormat(ele.Amount),
                                        };
                                    })}
                                    type="common"
                                />
                                <hr className="mb-20" />
                            </>
                        )}

                        <MoneyTextBoxEditor
                            onChange={(v) =>
                                onChange({ NotionalValueBeforeApril2006: { Amount: v, Currency: CurrencyCode.GBP } })
                            }
                            value={data.NotionalValueBeforeApril2006?.Amount}
                            currency={CurrencyCode.GBP}
                            cssWrapper="control-size-245"
                            label="Notional Value of Benefits from before 6 April 2006"
                        />
                        <h4>Benefit crystallisations performed off platform per tax year</h4>
                        <RadiosEditor
                            options={[true, false]}
                            cssWrapper={"rg-inline mb-10"}
                            onChange={(v) => {
                                this.setState({ hasChanged: true });
                                onChange({ BceOffPlatform: v ? [{ Amount: null, Year: 2006 }] : null });
                            }}
                            selected={data.BceOffPlatform && data.BceOffPlatform.length > 0 ? true : false}
                            label="Has any benefit crystallisation been performed off platform since 6 April 2006?"
                            getText={(o) => (o ? "Yes" : "No")}
                            required={"Please select"}
                        />
                        {data?.BceOffPlatform && data.BceOffPlatform.length > 0 && (
                            <>
                                {this.renderTaxYear(data.BceOffPlatform, "BceOffPlatform")}
                                <CircleBtn
                                    type={CircleBtnType.add}
                                    title="Add another tax year"
                                    onClick={() => {
                                        const _data = this.props.data.BceOffPlatform.concat();
                                        _data.push({ Amount: null, Year: null });
                                        this.props.onChange({ BceOffPlatform: _data });
                                        this.setState({ hasChanged: true });
                                    }}
                                />
                            </>
                        )}
                        {this.state.hasChanged && (
                            <Alert
                                alertType={AlertType.Warning}
                                message="You have altered some previous BCE data.  If your new entry is correct, further administrative action will be required in relation to the previous BCE.  Please refer the case to your technical team for guidance."
                            />
                        )}
                        <hr className="mb-20" />
                    </>
                )}
            </>
        );
    }
}
